import { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const DEFAULT_CAM = { x: 0.3, y: 0.3, z: 1.1 };
const SCALE = 0.008;

const WedgeBack3DPreview = ({
  backHeight = 40, length = 60, baseDepth = 15, topDepth = 5,
  fabricColor, fabricImageUrl, fabricZoom = 2, showZip = true, rawA = 0, rawB = 0, rawC = 0, rawD = 0, unit = 'cm',
  activeField = null, pipingOption = 'none', pipingColor = null, pipingImageUrl = null
}) => {
  const containerRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cushionRef = useRef(null);
  const zipGroupRef = useRef(null);
  const pipingLeftRef = useRef(null);
  const pipingRightRef = useRef(null);
  const sceneRef = useRef(null);
  const animFrameRef = useRef(null);
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const dimLabelsRef = useRef({ a: null, b: null, c: null, d: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const targetScaleYRef = useRef(1);
  const activeFieldRef = useRef(activeField);
  const groundRef = useRef(null);

  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);

  const color = fabricColor || '#5a8a5a';

  const dims = useMemo(() => ({
    h: Math.max(backHeight * SCALE, 0.05),
    l: Math.max(length * SCALE, 0.05),
    c: Math.max(baseDepth * SCALE, 0.05),
    d: Math.max(topDepth * SCALE, 0),
  }), [backHeight, length, baseDepth, topDepth]);

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(-0.06, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    const isMobileInit = rect.width < 500;
    controls.target.set(isMobileInit ? 0.09 : 0, isMobileInit ? -0.06 : -0.10, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(3, 5, 3);
    dirLight.castShadow = true;
    scene.add(dirLight);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    // Ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(5, 5),
      new THREE.ShadowMaterial({ opacity: 0.15 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    groundRef.current = ground;

    // Build initial cushion
    const geo = createWedgeGeometry(dims.h, dims.l, dims.c, dims.d);
    const mat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(color),
      roughness: 0.85,
      metalness: 0.02,
    });
    const cushion = new THREE.Mesh(geo, mat);
    cushion.castShadow = true;
    scene.add(cushion);
    cushionRef.current = cushion;
    currentDimsRef.current = { ...dims };
    targetDimsRef.current = { ...dims };

    // Zip on bottom along length (Z-axis)
    const zipMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    const zipTrack = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 1), zipMat);
    scene.add(zipTrack);
    const zipTeeth = [];
    for (let i = 0; i < 48; i++) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.006, 0.01), zipMat);
      scene.add(tooth);
      zipTeeth.push(tooth);
    }
    const zipPull = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.005, 0.03), zipMat);
    scene.add(zipPull);
    zipGroupRef.current = { track: zipTrack, teeth: zipTeeth, pull: zipPull };

    // Side piping meshes (left + right faces of the extrusion)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingLeft = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingRight = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingLeft.visible = false;
    pipingRight.visible = false;
    scene.add(pipingLeft);
    scene.add(pipingRight);
    pipingLeftRef.current = pipingLeft;
    pipingRightRef.current = pipingRight;
    sceneRef.current = scene;

    // Animation loop
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) return;

      // Smooth dimension lerping — rebuild geometry each frame
      const nextH = THREE.MathUtils.lerp(cd.h, td.h, 0.1);
      const nextL = THREE.MathUtils.lerp(cd.l, td.l, 0.1);
      const nextC = THREE.MathUtils.lerp(cd.c, td.c, 0.1);
      const nextD = THREE.MathUtils.lerp(cd.d, td.d, 0.1);

      if (Math.abs(nextH - cd.h) > 0.0001 || Math.abs(nextL - cd.l) > 0.0001 ||
          Math.abs(nextC - cd.c) > 0.0001 || Math.abs(nextD - cd.d) > 0.0001) {
        cushion.geometry.dispose();
        cushion.geometry = createWedgeGeometry(nextH, nextL, nextC, nextD);
        currentDimsRef.current = { h: nextH, l: nextL, c: nextC, d: nextD };
      }

      // Ground position at bottom of wedge
      ground.position.y = -nextH / 2 - 0.003;

      // Side piping Z tracking
      if (pipingLeftRef.current && pipingLeftRef.current.visible) {
        const pr = Math.min(nextH * 0.06, nextC * 0.08, 0.025);
        const pBevel = pr * 0.5;
        const pZ = nextL / 2 + pBevel * Math.SQRT1_2;
        pipingLeftRef.current.position.z = -pZ;
        pipingRightRef.current.position.z = pZ;
      }

      // Zip on bottom face of wedge, running along Z (length), centered on base depth
      if (zipGroupRef.current) {
        const cc = currentDimsRef.current;
        const { track, teeth, pull } = zipGroupRef.current;
        track.visible = showZip;
        teeth.forEach(t => t.visible = showZip);
        pull.visible = showZip;
        if (showZip) {
          const bottomY = -cc.h / 2 - 0.005;
          const zipLen = cc.l * 0.90;

          track.position.set(0, bottomY, 0);
          track.scale.set(1, 1, zipLen);

          const halfZip = zipLen / 2;
          const spacing = zipLen / teeth.length;
          for (let i = 0; i < teeth.length; i++) {
            const tz = -halfZip + (i + 0.5) * spacing;
            const xOff = (i % 2 === 0 ? 1 : -1) * 0.010;
            teeth[i].position.set(xOff, bottomY, tz);
          }
          pull.position.set(0, bottomY, halfZip + 0.01);
        }
      }

      // Camera reset — snap to fit entire cushion and labels
      if (resetCameraRef.current) {
        const cc = currentDimsRef.current;
        // Use diagonal extent for the wedge — height can dominate
        const maxDim = Math.max(cc.h, cc.l, cc.c, Math.sqrt(cc.h * cc.h + cc.c * cc.c));
        const isMobile = containerWidthRef.current < 500;
        if (isMobile) {
          const distMult = 1.8;
          const distBase = 0.35;
          const camScale = 1.4;
          const dist = maxDim * distMult + distBase;
          camera.position.set(
            DEFAULT_CAM.x / camScale * dist,
            DEFAULT_CAM.y / camScale * dist,
            DEFAULT_CAM.z / camScale * dist
          );
          controls.target.set(-0.07, -0.03, 0);
        } else {
          // Desktop: exact user-tuned pose.
          camera.position.set(0.4298, 0.3005, 1.0559);
          controls.target.set(-0.0624, -0.0997, 0.0251);
        }
        resetCameraRef.current = false;
        controls.saveState();
      }

      renderer.render(scene, camera);

      // Project dimension labels + highlight active field
      const af = activeFieldRef.current;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';
      const fieldMap = { backHeight: 'a', length: 'b', topDepth: 'c', baseDepth: 'd' };
      const activeLabel = fieldMap[af] || null;

      const labels = dimLabelsRef.current;
      if (labels.a && labels.b && labels.c) {
        const cRect = container.getBoundingClientRect();
        const project = (wp) => {
          const v = wp.clone().project(camera);
          return { x: (v.x * 0.5 + 0.5) * cRect.width, y: (-v.y * 0.5 + 0.5) * cRect.height, visible: v.z < 1 };
        };

        const setLabelHighlight = (el, key, pos) => {
          el.style.left = pos.x + 'px';
          el.style.top = pos.y + 'px';
          el.style.opacity = pos.visible ? '1' : '0';
          el.style.backgroundColor = activeLabel === key ? activeBg : defaultBg;
          el.style.color = activeLabel === key ? activeText : defaultText;
        };

        // A: back height
        const aP = project(new THREE.Vector3(-cd.c / 2 - 0.02, 0, cd.l / 2 + 0.02));
        setLabelHighlight(labels.a, 'a', aP);

        // B: length
        const bP = project(new THREE.Vector3(cd.c / 2 + 0.02, -cd.h / 2, 0));
        setLabelHighlight(labels.b, 'b', bP);

        // C: top depth
        if (labels.c && cd.d > 0) {
          const topMidX = (cd.d - cd.c) / 2;
          const cP = project(new THREE.Vector3(topMidX, cd.h / 2 + 0.02, cd.l / 2 + 0.02));
          setLabelHighlight(labels.c, 'c', cP);
        } else if (labels.c) {
          labels.c.style.opacity = '0';
        }

        // D: base depth
        if (labels.d) {
          const dP = project(new THREE.Vector3(0, -cd.h / 2 - 0.02, cd.l / 2 + 0.02));
          setLabelHighlight(labels.d, 'd', dP);
        }
      }
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set target dimensions on change — animation loop handles smooth lerp
  useEffect(() => {
    if (!targetDimsRef.current) return;
    targetDimsRef.current = { ...dims };
    resetCameraRef.current = true;
  }, [dims]);

  // Update color / texture
  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    }
  }, [color, fabricImageUrl]);

  // Update texture zoom
  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update side piping rings (trace the wedge cross-section outline on left/right faces)
  useEffect(() => {
    const pl = pipingLeftRef.current;
    const pr = pipingRightRef.current;
    if (!pl || !pr) return;

    const showPiping = pipingOption !== 'none';
    pl.visible = showPiping;
    pr.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    // Determine piping visual props
    const pColor = pipingOption === 'same' ? (fabricColor || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pl, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pr, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    // Build wedge outline for piping path
    const { h, l, c, d } = dims;
    const r = Math.min(h * 0.06, c * 0.08, 0.025);
    const bevel = r * 0.5;
    const tubeRadius = 0.003;

    // Recreate the same shape as createWedgeGeometry, but with rounded BACK corners
    // so the outward-normal expansion doesn't spike at the sharp 90° back edges
    const shape = new THREE.Shape();
    const backR = Math.min(r * 0.5, h * 0.03, c * 0.03);
    if (d > 0) {
      const slopeDx = d - c, slopeDy = h;
      const slopeLen = Math.sqrt(slopeDx * slopeDx + slopeDy * slopeDy);
      const snx = slopeDx / slopeLen, sny = slopeDy / slopeLen;
      // Start past the back-bottom corner (on the bottom edge)
      shape.moveTo(backR, 0);
      shape.lineTo(c - r, 0);
      shape.quadraticCurveTo(c, 0, c + snx * r, sny * r);
      shape.lineTo(d - snx * r, h - sny * r);
      shape.quadraticCurveTo(d, h, d - r, h);
      // Top edge to near back-top corner
      shape.lineTo(backR, h);
      // Round back-top corner
      shape.quadraticCurveTo(0, h, 0, h - backR);
      // Back edge down to near back-bottom corner
      shape.lineTo(0, backR);
      // Round back-bottom corner (closes the path back to start)
      shape.quadraticCurveTo(0, 0, backR, 0);
    } else {
      const peakR = Math.min(r, h * 0.04);
      const dx = c, dy = h;
      const slopeLen = Math.sqrt(dx * dx + dy * dy);
      shape.moveTo(backR, 0);
      shape.lineTo(peakR * (dx / slopeLen), h - peakR * (dy / slopeLen));
      shape.quadraticCurveTo(0, h, 0, h - peakR);
      shape.lineTo(0, backR);
      shape.quadraticCurveTo(0, 0, backR, 0);
    }

    // Get outline points (auto-samples curves)
    const pts2D = shape.getPoints(64);
    // Remove closing duplicate
    if (pts2D.length > 1 && pts2D[0].distanceTo(pts2D[pts2D.length - 1]) < 0.0001) pts2D.pop();

    // Convert to centered 3D coords (XY plane, Z=0)
    const pts3D = pts2D.map(p => new THREE.Vector3(p.x - c / 2, p.y - h / 2, 0));

    // Expand the path outward by bevelSize * cos(45°) so piping sits ON the bevel
    // surface rather than inside it (the ExtrudeGeometry bevel expands the XY outline)
    const expansion = bevel * Math.SQRT1_2;
    const n = pts3D.length;
    const expanded = pts3D.map((pt, i) => {
      const prev = pts3D[(i - 1 + n) % n];
      const next = pts3D[(i + 1) % n];
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      // Outward normal for CW winding: (dy, -dx)
      const nx = dy / len;
      const ny = -dx / len;
      return new THREE.Vector3(pt.x + nx * expansion, pt.y + ny * expansion, 0);
    });

    // LinearClosedCurve — linear interpolation between consecutive points, closed loop
    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t) {
        const n = this.pts.length;
        const f = t * n;
        const i = Math.floor(f) % n;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n], a);
      }
    }

    const curve = new LinearClosedCurve(expanded);
    const tubeGeo = new THREE.TubeGeometry(curve, 128, tubeRadius, 8, true);

    pl.geometry.dispose();
    pr.geometry.dispose();
    pl.geometry = tubeGeo;
    pr.geometry = tubeGeo.clone();

    // Position at side faces — bevel's 45° midpoint outward from the extrusion face
    const pipingZ = l / 2 + bevel * Math.SQRT1_2;
    pl.position.z = -pipingZ;
    pr.position.z = pipingZ;
  }, [dims, pipingOption, pipingColor, pipingImageUrl, fabricColor, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div
      className="w-full rounded-xl overflow-hidden relative"
      style={{ height: '100%', background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }}
      data-testid="wedge-back-3d-preview"
    >
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="three-canvas-container" />

      {/* Dimension labels */}
      {[
        { key: 'a', label: rawA > 0 ? `A: ${unit === 'mm' ? rawA * 10 : rawA} ${unit}` : 'A: Back Height' },
        { key: 'b', label: rawB > 0 ? `B: ${unit === 'mm' ? rawB * 10 : rawB} ${unit}` : 'B: Length' },
        { key: 'c', label: rawC > 0 ? `C: ${unit === 'mm' ? rawC * 10 : rawC} ${unit}` : (topDepth === 0 ? '' : 'C: Top Depth') },
        { key: 'd', label: rawD > 0 ? `D: ${unit === 'mm' ? rawD * 10 : rawD} ${unit}` : 'D: Base Depth' },
      ].map(({ key, label }) => (
        <div
          key={key}
          ref={el => dimLabelsRef.current[key] = el}
          className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
          style={{ opacity: 0, transition: 'opacity 0.15s' }}
          data-testid={`dim-label-${key}`}
        >
          {label}
        </div>
      ))}

      {/* Zip legend */}
      <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
        <svg width="24" height="6" viewBox="0 0 24 6" className="flex-shrink-0">
          <line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" />
        </svg>
        <span className="font-medium">Zip on bottom</span>
      </div>

      {/* Reset view */}
      <button
        onClick={handleReset}
        className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer"
        title="Reset view"
        data-testid="reset-view-btn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 0 1-15.36 6.36"/>
          <path d="M3 12a9 9 0 0 1 15.36-6.36"/>
          <polyline points="21 3 21 9 15 9"/>
          <polyline points="3 21 3 15 9 15"/>
        </svg>
      </button>

      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

function createWedgeGeometry(h, l, c, d) {
  // Cross-section in XY plane: back is at x=0, front at x=c
  // Bottom: y=0, Back top: y=h
  // Waterfall radius at top-right corner (where slope meets top)
  const r = Math.min(h * 0.06, c * 0.08, 0.025);
  const steps = 8;

  const shape = new THREE.Shape();
  // Start at back-bottom (0, 0)
  shape.moveTo(0, 0);
  // Bottom edge to front-bottom (stopping short for radius)

  if (d > 0) {
    // Front slope from (c, 0) up to near (d, h)
    // Radius at bottom-front corner where slope starts (c, 0)
    const slopeDx = d - c, slopeDy = h;
    const slopeLen = Math.sqrt(slopeDx * slopeDx + slopeDy * slopeDy);
    const snx = slopeDx / slopeLen, sny = slopeDy / slopeLen;
    shape.lineTo(c - r, 0);
    shape.quadraticCurveTo(c, 0, c + snx * r, sny * r);
    // Slope up to near top-front corner
    const slopeEndX = d - snx * r;
    const slopeEndY = h - sny * r;
    shape.lineTo(slopeEndX, slopeEndY);
    // Waterfall radius at top-front corner (d, h)
    shape.quadraticCurveTo(d, h, d - r, h);
    // Top edge to back-top
    shape.lineTo(0, h);
  } else {
    // Triangle: slope from (c, 0) up to (0, h)
    // Small radius at the peak
    const peakR = Math.min(r, h * 0.04);
    const dx = c, dy = h;
    const slopeLen = Math.sqrt(dx * dx + dy * dy);
    const nx = -dy / slopeLen, ny = dx / slopeLen;
    shape.lineTo(peakR * (dx / slopeLen), h - peakR * (dy / slopeLen));
    shape.quadraticCurveTo(0, h, 0, h - peakR);
  }
  // Close path back to start
  shape.lineTo(0, 0);

  const extrudeSettings = {
    depth: l,
    bevelEnabled: true,
    bevelThickness: r * 0.5,
    bevelSize: r * 0.5,
    bevelSegments: 4,
  };

  const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  // Center the geometry: offset so center is at origin
  geo.translate(-c / 2, -h / 2, -l / 2);
  remapExtrudeUVs(geo, 0.8, true);
  fixWedgeSideUVs(geo);
  geo.computeVertexNormals();
  return geo;
}

// Fix the 2 side faces (Z-facing) so fabric stripes run vertically (up/down).
// remapExtrudeUVs(rotateUV=true) gives Z-facing sides u=py, v=px (horizontal stripes).
// Swap to u=px, v=py so stripes run along height.
function fixWedgeSideUVs(geo) {
  const uvAttr = geo.attributes.uv;
  const posAttr = geo.attributes.position;
  const nAttr = geo.attributes.normal;
  if (!uvAttr || !posAttr || !nAttr) return;
  const inv = 1 / 0.8;
  for (let i = 0; i < uvAttr.count; i++) {
    const nzAbs = Math.abs(nAttr.getZ(i));
    if (nzAbs < 0.5) continue; // only fix Z-facing side vertices
    const px = posAttr.getX(i);
    const py = posAttr.getY(i);
    uvAttr.setXY(i, px * inv, py * inv);
  }
  uvAttr.needsUpdate = true;
}

export default WedgeBack3DPreview;
